/**
 * Store-scoping hooks, trimmed from the cockpit UI's lib/useStores.ts down to
 * what the app shell needs: who am I (which store, if any), and, for
 * corporate users, the list of stores to switch between. Real tool pages
 * (Today, Inventory, Questions, ...) will pull in the rest of that file's
 * hooks as each tool is built.
 *
 * Every endpoint lives under /v1/stores/* on the shared API. Under the binary
 * per-tool grant model (spec: Agents/specs/00_access-binary-grants.md) there
 * is a single tool slug, stores.sweetspot, gating Sweet Spot itself, held by
 * both store accounts and corporate (HQ/Ops) users alike. The two are told
 * apart at runtime, not by a separate permission:
 *   - a store account has a store_user row, so /v1/stores/me resolves without
 *     a store_no and comes back with role 'manager' | 'staff'.
 *   - a corporate user has no store_user row. Passing ?store_no= (any store
 *     from the active-store list) resolves them with role 'hq'; with no
 *     store_no picked yet, /v1/stores/me 403s with reason 'unmapped'.
 *
 * Assumption this relies on, that must hold on the api side: a store
 * account's own mapping always wins, so a stray store_no on a store
 * account's request is ignored rather than honored. Only a stores.sweetspot
 * holder with no store_user row may act on a store_no.
 *
 * Graceful degradation: any 404 (route not deployed) / 503 (feature not
 * configured) is treated as a quiet "not available yet" state, never a crash.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, type ApiError } from './api';
import { API_BASE } from './apiBase';
import { useSession } from './useSession';
import { useHqStore } from './useHqStore';

export const STORES_API_BASE = `${API_BASE}/v1/stores`;

// Whether the signed-in user can open Sweet Spot at all (store account or
// corporate). Does not by itself say which of the two they are.
//
// The api deliberately does NOT fold super_admin into `permissions` (see
// /v1/core/me in the api's tools/core/src/auth-routes.ts); every gate here
// must take the flag explicitly and check it at the call site, matching
// cockpit's convention (ui/src/lib/useSession.ts).
export function hasStoresSweetspot(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  if (!perms) return false;
  return perms.includes('stores.sweetspot');
}

// A 404 (route not yet deployed) or 503 (feature not configured) means this
// backend slice is not live. Pages render a quiet placeholder rather than error.
export function isNotAvailable(error: unknown): boolean {
  const status = (error as ApiError | null)?.status;
  return status === 404 || status === 503;
}

// The scope resolver returns 403 with reason 'unmapped' when the caller has no
// store assignment (a store user not yet linked, or an HQ user who has not
// picked a store).
export function isUnmapped(error: unknown): boolean {
  const e = error as ApiError | null;
  if (!e || e.status !== 403) return false;
  const body = e.body as { reason?: string; error?: { reason?: string } } | null;
  return (body?.error?.reason ?? body?.reason) === 'unmapped';
}

export type StoreRole = 'manager' | 'staff' | 'hq';

/**
 * One checklist item as the api ships it from GET /v1/stores/me.
 *
 * The field is `id`, NOT `item_id`. This type said `item_id` until 2026-08-13,
 * which meant every item read as `undefined` and the whole checklist shared one
 * slot: ticking any box ticked them all, and the submitted entries carried no
 * usable id so the api discarded them. Only the SUBMIT payload uses `item_id`
 * (see ReportChecklistEntry), which is why the two names sit side by side.
 */
export type StoreChecklistItem = { id: number; label: string };

export type StoreMe = {
  store: {
    store_no: number;
    store_name: string;
    region: string | null;
    district: string | null;
    // IANA zone (e.g. 'America/Los_Angeles'), or null if not yet configured.
    // The api resolves every store-scoped "business day" from this same
    // column (retail_stores.timezone); the client mirrors it for the same
    // default rather than assuming America/Denver for every store.
    timezone: string | null;
  } | null;
  role: StoreRole;
  // Per-phase checklist definitions sourced from store_checklist_item on the
  // api. Optional so consumers not touching Today keep compiling if the api
  // omits the field.
  checklist?: {
    open: StoreChecklistItem[];
    midday: StoreChecklistItem[];
    close: StoreChecklistItem[];
  };
};

// `storeNo` is the corporate path: a stores.sweetspot holder with no
// store_user mapping may act on any store by passing ?store_no=. Store
// accounts omit it (or have it ignored server-side) and always resolve to
// their own mapped store.
export function useStoreMe(storeNo?: number): UseQueryResult<StoreMe, Error> {
  return useQuery<StoreMe, Error>({
    queryKey: ['stores', 'me', storeNo ?? 'own'],
    queryFn: () => {
      const qs = storeNo !== undefined ? `?store_no=${storeNo}` : '';
      return apiGet<StoreMe>(`${STORES_API_BASE}/me${qs}`);
    },
    staleTime: 60_000,
    retry: false,
  });
}

export type StoreListRow = { store_no: number; store_name: string };

// How a store is named everywhere it is picked or headed: number, then name
// (for example "12 - Cherry Creek"). Falls back to whichever half exists.
export function storeLabel(storeNo: number | string | null | undefined, storeName: string | null | undefined): string {
  const no = storeNo === null || storeNo === undefined ? '' : String(storeNo);
  return [no, storeName?.trim() ?? ''].filter(Boolean).join(' - ');
}

export type ScorecardResponse = { date: string; stores: StoreListRow[] };

// The corporate store switcher reuses the scorecard's active-store list (the
// same source the cockpit stores pages' HqStoreSelect picker uses), rather
// than a dedicated "list stores" endpoint.
export function useStoreList(): UseQueryResult<ScorecardResponse, Error> {
  return useQuery<ScorecardResponse, Error>({
    queryKey: ['stores', 'scorecard', 'today'],
    queryFn: () => apiGet<ScorecardResponse>(`${STORES_API_BASE}/hq/scorecard`),
    staleTime: 15_000,
    retry: false,
  });
}

// Tells apart a store account from a corporate user and, for a corporate
// user, carries the store they currently have selected (persisted via
// useHqStore/localStorage). See the file header for how the two are told
// apart: there is no separate permission for it under the binary grant model,
// only what /v1/stores/me resolves to.
export type StoreAssignment =
  | { kind: 'loading' }
  // No stores.sweetspot grant at all: nothing to show.
  | { kind: 'none' }
  // A store account: locked to their own mapped store, no picker.
  | { kind: 'store'; storeNo: number; storeName: string }
  // A corporate user: storeNo/storeName reflect the current header
  // selection, undefined until one has been picked.
  | { kind: 'corporate'; storeNo: number | undefined; storeName: string | undefined };

export function useStoreAssignment(): StoreAssignment {
  const { user } = useSession();
  const hasAccess = hasStoresSweetspot(user?.permissions, user?.superAdmin);
  const { storeNo: pickedStoreNo } = useHqStore();
  const meQ = useStoreMe(pickedStoreNo ?? undefined);

  if (!hasAccess) return { kind: 'none' };
  if (meQ.isLoading) return { kind: 'loading' };
  if (meQ.data?.store) {
    const { store_no, store_name } = meQ.data.store;
    return meQ.data.role === 'hq'
      ? { kind: 'corporate', storeNo: store_no, storeName: store_name }
      : { kind: 'store', storeNo: store_no, storeName: store_name };
  }
  if (isUnmapped(meQ.error)) {
    return { kind: 'corporate', storeNo: undefined, storeName: undefined };
  }
  // Any other error (network, 404/503 route not deployed, etc.) - nothing to
  // show rather than a crash; pages that need the data surface their own
  // error state via useStoreMe directly.
  return { kind: 'none' };
}

// The store_no a corporate user is currently acting on (from the store
// switcher), or undefined for a store account (who resolves via their own
// store_user mapping) and for a corporate user who has not picked a store
// yet. Every store-scoped hook should pass this so corporate users resolve a
// store instead of getting a 403.
export function useEffectiveStoreNo(): number | undefined {
  const assignment = useStoreAssignment();
  return assignment.kind === 'corporate' ? assignment.storeNo : undefined;
}
